import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { textStyles } from '../../styles/textstyle';
import { CartCalculation } from '../../types/shop';
import ProductDiscount from './ProductDiscount';
import { getProductName } from '../../utils/productUtils';


interface CheckoutProductListProps {
  cartCalculation: CartCalculation;
}

const CheckoutProductList: React.FC<CheckoutProductListProps> = ({ cartCalculation }) => {
  const { i18n } = useTranslation();
  const currency = cartCalculation.currency || 'HK$';

  return (
    <>
      {/* Product List (read-only, no quantity change or remove) */}
      {Array.isArray(cartCalculation.items) &&
        cartCalculation.items.length > 0 && (
          <Box mt={0.5}>
            <Typography sx={textStyles.h6} mb={2}>
              Total Items
            </Typography>
            {cartCalculation.items.map(
              (item) => (
                <Paper
                  key={item.productId}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                    boxShadow: 'none',
                    border: 'none',
                  }}
                >
                  <img
                    src={item.product.imageUrl || '/images/img_placeholder.svg'}
                    alt={item.product.name}
                    style={{
                      width: 90,
                      height: 90,
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                    onError={e => {
                      e.currentTarget.src = '/images/img_placeholder.svg';
                    }}
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    flex={1}
                    minWidth={0}
                  >
                    <Typography mb={0.5} sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: 0,
                        verticalAlign: 'middle',
                    }}>
                      {getProductName(item.product, i18n.language)}
                    </Typography>
                    
                    {/* Selected Variants */}
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                        {Object.entries(item.selectedOptions).map(([optionKey, optionValue]) => (
                          <Chip
                            key={optionKey}
                            label={`${optionKey}: ${optionValue}`}
                            size="small"
                            sx={{
                              backgroundColor: '#F5F5F5',
                              color: '#666',
                              fontSize: '12px',
                              height: '20px',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    <Typography fontSize={14} fontWeight={400} color="text.primary" mb={0.5}>
                      QTY: {item.quantity}
                    </Typography>
                    
                    {/* Product-specific Discounts */}
                    <ProductDiscount
                      appliedDiscounts={cartCalculation.validatedDiscounts?.appliedDiscounts || []}
                      currency={currency}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" justifyContent="center" minWidth={100}>
                    {item.totalComparePrice > item.totalPrice && item.savings > 0 && (
                      <>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color="#B3907A"
                          sx={{ textDecoration: 'line-through' }}
                          mb={0.5}
                        >
                          {currency}
                          {item.totalComparePrice?.toFixed(2)}
                        </Typography>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color="#4CAF50"
                          mb={0.5}
                        >
                          Save {currency}{item.savings.toFixed(2)}
                        </Typography>
                      </>
                    )}
                    <Typography fontSize={16} fontWeight={700} color="#17110D">
                      {currency}
                      {item.totalPrice?.toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              )
            )}
          </Box>
        )}
    </>
  );
};

export default CheckoutProductList; 